import { CommonService } from "@shared/services/common";
import { Component, OnInit, Input, ViewContainerRef, ViewChild } from "@angular/core";
import { geometry, Group, Layout, drawing } from "@progress/kendo-drawing";
import { RenderEvent, SeriesLabels } from "@progress/kendo-angular-charts";
import { groupBy, DataSourceRequestState } from "@progress/kendo-data-query";
import { ISubscription } from "rxjs/Subscription";
import { DatePipe } from "@angular/common";
import { widgetType } from "@fs/enums/widget-type";
import { presetRangeType } from "@fs/enums/preset-range-type";
import { TabOpenService } from "src/app/services/tab-open.service";
import { DashboardModel, WorkOrderDashboard } from "@fs/models/dashboard/dashboard.model";
import { CommonMethods } from "@services/common.method";
import { SpinnerService } from "src/app/shared/services/spinner.service";
import { Constants } from "src/app/shared/models/constants";
import { LoaderType } from "src/app/shared/enums/common";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
  providers: [DatePipe]
})
export class DashboardComponent implements OnInit {
  @Input() tabCounter: number;
  @ViewChild("workorderchart", {
    read: ViewContainerRef
  }) workorderchart: ViewContainerRef;
  @ViewChild("customerchart", {
    read: ViewContainerRef
  }) customerchart: ViewContainerRef;
  @ViewChild("technicianchart", {
    read: ViewContainerRef
  }) technicianchart: ViewContainerRef;
  @ViewChild("totalchart", {
    read: ViewContainerRef
  }) totalchart: ViewContainerRef;
  // #region  Private Variables /
  private center: any;
  private radius: number;
  private subscriptions: ISubscription[] = [];
  // #endregion /

  // #region  Public Variables
  public CompleteData: any[];
  public BarChartColors: any;
  public TechnicianAverageOrders: any[];
  public TechnicianData: any[];
  public TechnicianNames: string[];
  public TechnicianTotalOrders: any[];
  public TotalRevenueData: any[];
  public WorkOrdersData: any[];
  public SubStatesData: any[];
  public newCustomers: number = 0;
  public totalCustomers: number = 0;
  public subStateHeaderText: string = "";
  public totalRevenue: string = "0";
  // Necessary to bind `this` to the AppComponent instance
  public visualHandler = this.visual.bind(this);
  public options: {
    dateFrom: string,
    dateTo: string,
    type: string
  };
  public detailSeries = [];
  public SeriesLabels: SeriesLabels = {
    visible: true, // Note that visible defaults to false
    padding: 3,
    font: "normal 12px Lato, sans-serif"
  };
  public presetRange = [
    { text: 'Today', value: 'today' },
    { text: 'This week', value: 'this week' },
    { text: 'Last week', value: 'last week' },
    { text: 'This month', value: 'this month' },
    { text: 'Last month', value: 'last month' },
    { text: 'This year', value: 'this year' },
    { text: 'Last year', value: 'last year' }
  ]
  public defaultPresetRange: string = "today";
  public workOrderSelection: any = { today: false, thisWeek: false, lastWeek: false, thisMonth: true, lastMonth: false, thisYear: false, lastYear: false };
  public technicianSelection: any = { today: false, thisWeek: false, lastWeek: false, thisMonth: true, lastMonth: false, thisYear: false, lastYear: false };
  public workOrderWidget: string = this.defaultPresetRange;
  public customerWidget: string = this.defaultPresetRange;
  public technicianWidget: string = this.defaultPresetRange;
  public widgetDataDic: DashboardModel = new DashboardModel();
  public showWorkOrderLoader: boolean = false;
  public showCustomerLoader: boolean = false;
  public showUserLoader: boolean = false;
  // #endregion
  // #region  LifeCycle Events
  constructor(private tabopen: TabOpenService, private httpCommonService: CommonService, private datePipe: DatePipe,
    private commonmethods: CommonMethods,
    private showspinner: SpinnerService,
    private viewContainerRef: ViewContainerRef) {
    this.WorkOrdersData = [];
    let subscription = this.tabopen.focusTabEvent.subscribe(response => {
      if (response.tabCounter == this.tabCounter) {
        this.RefreshDashboard();
      }
    });
    this.subscriptions.push(subscription);
  }
  ngOnInit() {
    let option = { dateFrom: '', dateTo: '', widgetType: '', presetRange: this.defaultPresetRange };
    this.EvalutePresetRangeDate(option);
    this.getKPIData(option.dateFrom, option.dateTo, option.widgetType);
  }
  ngOnDestroy() {
    if (this.subscriptions != null && this.subscriptions != undefined) {
      this.subscriptions.forEach(element => {
        element.unsubscribe();
      });
    }
  }
  // #endregion
  // #region  Private Methods
  sortByProperty = function (property) {
    return function (x, y) {
      return x[property] === y[property]
        ? 0
        : x[property] > y[property]
          ? 1
          : -1;
    };
  };
  public GetColor(val) {
    var baseColors = [
      { status: "cancelled", color: "#fc0313" },
      { status: "open", color: "#1B75BB" },
      { status: "coil", color: "#EEAAEE" },
      { status: "action required", color: "#5ef7e8" },
      { status: "completed", color: "#F7A35C" },
      { status: "void", color: "#fc9105" }
    ];
    var indexOfColor = baseColors.findIndex(
      x => x.status.toString().toLowerCase() == val.toString().toLowerCase()
    );
    if (indexOfColor > -1) return baseColors[indexOfColor].color;
    else return "#008cff";
  }
  private getKPIData(dateFrom = '', dateTo = '', widget = '', spinnerTimeout = Constants.defaultSpinnertimeout) {   
    this.BarChartColors = {
      TotalOrders: "#1B75BB",
      AvgOrders: "#9D9D9D"
    };
    // Temp changes
    if (dateFrom == '' && dateTo == '' && widget == '') {
      let endDate = new Date();
      endDate.setHours(23, 59, 59, 0);
      let startDate = new Date();
      startDate.setDate(endDate.getDate() - 59);
      startDate.setHours(0, 0, 0, 0);
      this.options = {
        dateFrom: this.datePipe.transform(startDate, "yyyy-MM-dd HH!mm!ss"),
        dateTo: this.datePipe.transform(endDate, "yyyy-MM-dd HH!mm!ss"),
        type: ""
      }
    }
    else {
      this.options = {
        dateFrom: this.datePipe.transform(dateFrom, "yyyy-MM-dd HH!mm!ss"),
        dateTo: this.datePipe.transform(dateTo, "yyyy-MM-dd HH!mm!ss"),
        type: widget
      }
    }
    let methodTabCounter_workorder: number = this.commonmethods.generateDynamictabCounter();
    let methodTabCounter_customer: number = this.commonmethods.generateDynamictabCounter();
    let methodTabCounter_technician: number = this.commonmethods.generateDynamictabCounter();
    let methodTabCounter_total: number = this.commonmethods.generateDynamictabCounter();
    if (this.options.type == '' || this.options.type == widgetType.workOrder) {
      this.showspinner.showloader(this.workorderchart, methodTabCounter_workorder, spinnerTimeout, false, 0, LoaderType.circle, [{ "class": "sm-size chart wt-chart" }]);
    }

    if (this.options.type == '' || this.options.type == widgetType.customer) {
      this.showspinner.showloader(this.customerchart, methodTabCounter_customer, spinnerTimeout, false, 0, LoaderType.circle, [{ "class": "xsm-size chart cust-chart" }]);
    }

    if (this.options.type == '' || this.options.type == widgetType.technician) {
      this.showspinner.showloader(this.totalchart, methodTabCounter_total, spinnerTimeout, false, 0, LoaderType.circle, [{ "class": "xsm-size chart user-chart" }]);
      this.showspinner.showloader(this.technicianchart, methodTabCounter_technician, spinnerTimeout, false, 0, LoaderType.circle, [{ "class": "sm-size chart user-chart" }]);
    }

    let _this = this;
    let subscription = this.httpCommonService
      .GetKPIData(this.options)
      .subscribe(response => {
        if (response != null) {
          if (_this.options.type == '' || _this.options.type == widgetType.customer)
            this.setCustomerWidgetData(response);

          if (_this.options.type == '' || _this.options.type == widgetType.workOrder)
            this.setWorkOrderWidgetData(response);

          if (_this.options.type == '' || _this.options.type == widgetType.technician)
            this.setTechnicianWidgetData(response);
        }
        _this.hideLoader(methodTabCounter_workorder, methodTabCounter_customer, methodTabCounter_technician, methodTabCounter_total);
      }, error => {
        _this.hideLoader(methodTabCounter_workorder, methodTabCounter_customer, methodTabCounter_technician, methodTabCounter_total);
      });
    this.subscriptions.push(subscription);
  }
  private hideLoader(methodTabCounter_workorder: number, methodTabCounter_customer: number, methodTabCounter_technician: number, methodTabCounter_total: number) {
    if (this.options.type == '' || this.options.type == widgetType.workOrder) {
      this.showspinner.hideLoader(this.workorderchart, methodTabCounter_workorder);
    }

    if (this.options.type == '' || this.options.type == widgetType.customer) {
      this.showspinner.hideLoader(this.customerchart, methodTabCounter_customer);
    }

    if (this.options.type == '' || this.options.type == widgetType.technician) {
      this.showspinner.hideLoader(this.totalchart, methodTabCounter_total);
      this.showspinner.hideLoader(this.technicianchart, methodTabCounter_technician);
    }
  }
  private getKPIWorkOrderData(dateFrom = '', dateTo = '', widget = '') {
    this.showWorkOrderLoader = true;
    // we need to show its inline spinner
    let methodTabCounter: number = this.commonmethods.generateDynamictabCounter();
    this.showspinner.showloader(this.workorderchart, methodTabCounter, Constants.defaultSpinnertimeout, false, 0, LoaderType.circle, [{ "class": "sm-size chart wt-chart" }]);
    let optionWorkOrder = {
      dateFrom: this.datePipe.transform(dateFrom, "yyyy-MM-dd HH!mm!ss"),
      dateTo: this.datePipe.transform(dateTo, "yyyy-MM-dd HH!mm!ss"),
      type: widget
    }
    let _this = this;
    let subscription = this.httpCommonService
      .GetKPIData(optionWorkOrder)
      .subscribe(response => {
        if (response != null)
          _this.setWorkOrderWidgetData(response);

        _this.showspinner.hideLoader(this.workorderchart, methodTabCounter);
      }, (error: any) => { _this.showspinner.hideLoader(this.workorderchart, methodTabCounter); });
    this.subscriptions.push(subscription);
  }

  private getKPICustomerData(dateFrom = '', dateTo = '', widget = '') {
    this.showCustomerLoader = true;
    let methodTabCounter: number = this.commonmethods.generateDynamictabCounter();
    this.showspinner.showloader(this.customerchart, methodTabCounter, Constants.defaultSpinnertimeout, false, 0, LoaderType.circle, [{ "class": "xsm-size chart cust-chart" }]);
    let optionCustomer = {
      dateFrom: this.datePipe.transform(dateFrom, "yyyy-MM-dd HH!mm!ss"),
      dateTo: this.datePipe.transform(dateTo, "yyyy-MM-dd HH!mm!ss"),
      type: widget
    }
    let _this = this;
    let subscription = this.httpCommonService
      .GetKPIData(optionCustomer)
      .subscribe(response => {
        if (response != null)
        _this.setCustomerWidgetData(response);

        _this.showspinner.hideLoader(this.customerchart, methodTabCounter);
      }, (error: any) => { _this.showspinner.hideLoader(this.customerchart, methodTabCounter); });
    this.subscriptions.push(subscription);
  }

  private getKPITechnicianData(dateFrom = '', dateTo = '', widget = '') {
    this.showUserLoader = true;
    let techTabCounter: number = this.commonmethods.generateDynamictabCounter();
    let totalTabCounter: number = this.commonmethods.generateDynamictabCounter();
    this.showspinner.showloader(this.totalchart, techTabCounter, Constants.defaultSpinnertimeout, false, 0, LoaderType.circle, [{ "class": "xsm-size chart user-chart" }]);
    this.showspinner.showloader(this.technicianchart, totalTabCounter, Constants.defaultSpinnertimeout, false, 0, LoaderType.circle, [{ "class": "sm-size chart user-chart" }]);
    let optionTechnician = {
      dateFrom: this.datePipe.transform(dateFrom, "yyyy-MM-dd HH!mm!ss"),
      dateTo: this.datePipe.transform(dateTo, "yyyy-MM-dd HH!mm!ss"),
      type: widget
    }

    let _this = this;
    let subscription = this.httpCommonService
      .GetKPIData(optionTechnician)
      .subscribe(response => {
        if (response != null)
        _this.setTechnicianWidgetData(response);

        _this.showspinner.hideLoader(this.totalchart, totalTabCounter);
        _this.showspinner.hideLoader(this.technicianchart, techTabCounter);
      }, (error: any) => { _this.showspinner.hideLoader(this.totalchart, totalTabCounter);_this.showspinner.hideLoader(this.technicianchart, techTabCounter); });
    this.subscriptions.push(subscription);
  }

  public ShowSubStatusOfCategory(category: string) {
    this.subStateHeaderText = "Work Order State - " + category;

    // Filter out States and Substates
    var items = this.widgetDataDic.WorkOrder; // Already filled on load
    var result_states = [];
    var lookup = [];
    if (category.toLowerCase() != 'cancelled') {
      if (items != undefined) {
        for (let i = 0; i < items.length; i++) {
          let item = items[i];
          if (item.WorkOrderStatus.toString().toLowerCase() == category.toString().toLowerCase()) {
            if (!(item.WorkOrderSubStatus in lookup)) {
              lookup[item.WorkOrderSubStatus] = "1";
              item["color"] = this.GetColor(category);
              item["Sub Status"] = item.TotalCalls;
              result_states.push(item);
            }
          }
        }
      }
    }
    else {
      var items = items.filter(x => x.WorkOrderStatus.toString().toLowerCase() ==
        category.toString().toLowerCase());
      let totalCalls = 0;
      if (items != undefined) {
        items.forEach(function (value) {
          totalCalls += value.TotalCalls;
        });
      }
      let item = { 'color': this.GetColor(category), 'Sub Status': totalCalls, 'WorkOrderSubStatus': 'Cancelled', 'WorkOrderStatus': 'Cancelled' }
      result_states.push(item);
    }
    result_states = result_states.sort(
      this.sortByProperty("WorkOrderSubStatus")
    );
    this.SubStatesData = result_states;
  }

  private GetRevenue() {
    if (this.TotalRevenueData != undefined) {
      let Total_Orders = this.TotalRevenueData.find(
        x => x.Key == "Total Orders"
      ).Value;
      this.totalRevenue = Total_Orders;
    }
  }

  private setCustomerWidgetData(res) {
    this.widgetDataDic.Customer = res.CompanyKPIsCustomerCount;
    if (!CommonMethods.CheckNullOrUndefined(this.widgetDataDic.Customer, true)) {
      this.newCustomers = this.widgetDataDic.Customer.length > 0 ? this.widgetDataDic.Customer[0].NewCustomerCount : 0;
      this.totalCustomers = this.widgetDataDic.Customer.length > 0 ? this.widgetDataDic.Customer[0].TotalCustomerCount : 0;
    }
    else {
      this.newCustomers = 0;
      this.totalCustomers = 0;
    }
  }

  private setWorkOrderWidgetData(res) {
    this.widgetDataDic.WorkOrder = res.CompanyKPIsWorkOrder;
    var result_states = [];
    if (!CommonMethods.CheckNullOrUndefined(this.widgetDataDic.WorkOrder, true)) {
      let items = this.widgetDataDic.WorkOrder;
      for (let i = 0; i < items.length; i++) {
        let item = items[i];
        if (item.WorkOrderStatus.toString().toLowerCase() != "void") {
          if (
            result_states.findIndex(data => data.WorkOrderStatus == item.WorkOrderStatus) > -1
          ) {
            let elementIndex = result_states.findIndex(data => data.WorkOrderStatus == item.WorkOrderStatus);
            result_states[elementIndex].TotalCalls = parseInt(result_states[elementIndex].TotalCalls) + item.TotalCalls;
          } else {
            let element: WorkOrderDashboard = new WorkOrderDashboard();
            element.WorkOrderStatus = item.WorkOrderStatus;
            element.TotalCalls = item.TotalCalls;
            element.WorkOrderSubStatus = item.WorkOrderSubStatus;
            element.color = this.GetColor(item.WorkOrderStatus);
            result_states.push(element);
          }
        }
      }
    }
    result_states = result_states.sort(
      this.sortByProperty("WorkOrderStatus")
    );
    this.WorkOrdersData = result_states;
    // show substates open state
    this.ShowSubStatusOfCategory("Open");
  }

  private setTechnicianWidgetData(res) {
    this.widgetDataDic.Technician = res.CompanyKPIsTechnicianPerf;
    if (!CommonMethods.CheckNullOrUndefined(this.widgetDataDic.Technician)) {
      let techData = this.httpCommonService.GetTechPerfomanceGraphResultSet(
        this.widgetDataDic.Technician
      );
      this.TotalRevenueData = this.httpCommonService.GetTotalOrderGraphResultSet(techData.totalOrders, techData.avgOrders);
      this.GetRevenue()
      this.detailSeries = groupBy(techData.data, [{ field: "type" }]);
    }
    else {
      this.TotalRevenueData = [];
      this.totalRevenue = "0";
      this.detailSeries = groupBy([], [{ field: "type" }]);
    }
  }

  // #endregion
  // #region  Public Methods
  public visual(e: any): Group {
    if (e.dataItem.dummy) {
      return null;
    }
    this.center = e.center;
    this.radius = e.radius;
    return e.createVisual();
  }
  public DonutLabelValue(e: any): string {
    if (e.dataItem.dummy) {
      return null;
    } else return e.value;
  }
  public RenderHandler(e: RenderEvent): void {
    const circleGeometry = new geometry.Circle(this.center, this.radius);
    const bbox = circleGeometry.bbox();
    const CircleCenter_bbox = circleGeometry
      .bbox()
      .setSize([bbox.width(), bbox.height() - 30]);
    if (this.TotalRevenueData != undefined) {
      let Total_Orders = this.TotalRevenueData.find(
        x => x.Key == "Total Orders"
      ).Value;
      let Average_Orders = this.TotalRevenueData.find(
        x => x.Key == "Total Orders"
      ).Value;
      let Order_Count = Number(Total_Orders) + Number(Average_Orders);
      const leftLine = new drawing.Text(
        "$" + "0.00",
        [bbox.origin.x, bbox.origin.y + bbox.size.height / 2],
        {
          font: "10px Verdana,Arial,sans-serif"
        }
      );
      const MiddleLine = new drawing.Text("$" + Total_Orders + "K", [0, 0], {
        font:
          Total_Orders.toString().length + 2 >= 19
            ? "13px Verdana,Arial,sans-serif"
            : "15px Verdana,Arial,sans-serif"
      });
      let Order_Count_Length = (Order_Count.toString().length + 2) * 6;
      let RightPadding =
        Order_Count_Length > 50
          ? Order_Count_Length
          : (50 - Order_Count_Length) / 2 + Order_Count_Length;
      const RightLine = new drawing.Text(
        "$" + Order_Count + "K ",
        [
          bbox.origin.x + bbox.size.width - RightPadding,
          bbox.origin.y + bbox.size.height / 2
        ],
        {
          font: "10px Verdana,Arial,sans-serif"
        }
      );
      const layout = new Layout(CircleCenter_bbox, {
        alignContent: "center",
        alignItems: "center",
        justifyContent: "center",
        spacing: 0,
        lineSpacing: 10
      });
      layout.append(MiddleLine);
      layout.reflow();
      e.sender.surface.draw(layout);
      e.sender.surface.draw(leftLine);
      e.sender.surface.draw(RightLine);
    }
  }
  public RefreshDashboard() {
    let optionworkOrder = { dateFrom: '', dateTo: '', widgetType: widgetType.workOrder, presetRange: this.workOrderWidget };
    this.EvalutePresetRangeDate(optionworkOrder);
    this.getKPIWorkOrderData(optionworkOrder.dateFrom, optionworkOrder.dateTo, optionworkOrder.widgetType);
    let optionCustomer = { dateFrom: '', dateTo: '', widgetType: widgetType.customer, presetRange: this.customerWidget };
    this.EvalutePresetRangeDate(optionCustomer);
    this.getKPICustomerData(optionCustomer.dateFrom, optionCustomer.dateTo, optionCustomer.widgetType);
    let optionTechnician = { dateFrom: '', dateTo: '', widgetType: widgetType.technician, presetRange: this.technicianWidget };
    this.EvalutePresetRangeDate(optionTechnician);
    this.getKPITechnicianData(optionTechnician.dateFrom, optionTechnician.dateTo, optionTechnician.widgetType);
  }
  public showSubState(e: any) {
    this.ShowSubStatusOfCategory(e.category);
  }
  public workOrderWidgetChanged(e) {
    let option = { dateFrom: '', dateTo: '', widgetType: widgetType.workOrder, presetRange: this.workOrderWidget };
    this.EvalutePresetRangeDate(option);
    this.getKPIData(option.dateFrom, option.dateTo, option.widgetType, 0);
  }
  public technicianWidgetChanged(e) {
    let option = { dateFrom: '', dateTo: '', widgetType: widgetType.technician, presetRange: this.technicianWidget };
    this.EvalutePresetRangeDate(option);
    $(".main__content_section").animate({ scrollTop: $(".main__content_section").height() }, 1000);
    this.getKPIData(option.dateFrom, option.dateTo, option.widgetType, 0);
  }
  public customerWidgetChanged(e) {
    let option = { dateFrom: '', dateTo: '', widgetType: widgetType.customer, presetRange: this.customerWidget };
    this.EvalutePresetRangeDate(option);
    this.getKPIData(option.dateFrom, option.dateTo, option.widgetType, 0);
  }
  public EvalutePresetRangeDate(option) {
    let endDate: Date;
    let startDate: Date;
    let curr: Date = new Date();
    let day: number;
    switch (option.presetRange) {
      case presetRangeType.today:
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(endDate.getDate());
        break;
      case presetRangeType.thisWeek:
        day = curr.getDay();
        startDate = new Date(curr.getTime() - 60 * 60 * 24 * (day - 1) * 1000); // will return firstday (i.e. Monday) of the week
        endDate = new Date(startDate.getTime() + 60 * 60 * 24 * 6 * 1000); // adding (60*60*6*24*1000) means adding six days to the firstday which results in lastday (Saturday) of the week
        break;
      case presetRangeType.lastWeek:
        day = curr.getDay();
        startDate = new Date(curr.getTime() - 60 * 60 * 24 * (day + 6) * 1000); // will return firstday (i.e. Monday) of the week
        endDate = new Date(startDate.getTime() + 60 * 60 * 24 * 6 * 1000); // adding (60*60*6*24*1000) means adding six days to the firstday which results in lastday (Saturday) of the week
        break;
      case presetRangeType.thisMonth:
        endDate = new Date(curr.getFullYear(), curr.getMonth() + 1, 0)
        startDate = new Date(curr.getFullYear(), curr.getMonth(), 1);
        break;
      case presetRangeType.lastMonth:
        endDate = new Date(curr.getFullYear(), curr.getMonth(), 0)
        startDate = new Date(curr.getFullYear(), curr.getMonth() - 1, 1);
        break;
      case presetRangeType.thisYear:
        endDate = new Date(curr.getFullYear() + 1, 0, 0)
        startDate = new Date(curr.getFullYear(), 0, 1);
        break;
      case presetRangeType.lastYear:
        endDate = new Date(curr.getFullYear(), 0, 0);
        startDate = new Date(curr.getFullYear() - 1, 0, 1);
        break;

    }
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 0);
    option.dateFrom = startDate;
    option.dateTo = endDate;
  }
  // #endregion
}
